import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { Form, InputGroup } from "react-bootstrap";
import ImageUpload from "../../components/ImageUpload";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";
import * as CustomToast from "../../components/CustomToastMessage";
import MyEditor from "../../components/MyEditor";
import MultipleSelectDropdown from "../../components/MultipleSelectDropdown";

function ProductsEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    link: "",
    categories: [],
    // category: "",
    currency: "",
  });
  const [images, setImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/product/${id}`
        );
        const product = response.data.data;
        setFormData({
          name: product.name,
          link: product.link,
          categories: Array.isArray(product?.categories)
            ? product?.categories?.map((item) => ({
                value: item._id,
                label: item.name,
              })) || []
            : [],
          //   category: product.category?._id || "",
          currency: product.currency,
        });
        const existingImages = (product.productImage || []).map((img) => ({
          url: `${process.env.REACT_APP_BACKEND_URL}/${img}`,
          path: img,
          isNew: false,
          file: null,
        }));
        setImages(existingImages);
      } catch (error) {
        if (error.response?.data?.status === 400) {
          CustomToast.error(error.response?.data?.message);
        }
        console.error(error.response?.data?.message || "Internal server error");
        navigate("/products");
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/category/list`,
          {
            params: { page: 1, limit: 1000, search: "" },
          }
        );
        if (response.data.success) {
          setCategories(response.data.data);
        } else {
          setCategories([]);
        }
      } catch (error) {
        setCategories([]);
      }
    };
    if (id) fetchProduct();
    fetchCategories();
  }, [id, navigate]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((image) => {
        if (image.isNew) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [images]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "price" || name === "discount") {
      const numericValue = value.replace(/[^0-9.,]/g, "");
      const parts = numericValue.split(".");
      const sanitizedValue =
        parts[0] + (parts.length > 1 ? "." + parts[1] : "");
      setFormData({
        ...formData,
        [name]: sanitizedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleImageSelect = (selectedFiles) => {
    const newImages = selectedFiles.map((file) => ({
      url: URL.createObjectURL(file),
      isNew: true,
      file: file,
    }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const handleImageDelete = (index) => {
    const imageToDelete = images[index];

    if (!imageToDelete.isNew) {
      // It's an existing image, add its path to removedImages
      setRemovedImages((prev) => [...prev, imageToDelete.path]);
    } else {
      // It's a new image, revoke its blob URL
      URL.revokeObjectURL(imageToDelete.url);
    }

    // Remove the image from the display list
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!images?.length) {
      CustomToast.error("Image is required");
      return;
    }

    if (!formData.categories.length) {
      CustomToast.error("Please select at least one category");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("link", formData.link);
    // formDataToSend.append("category", formData.category);
    // formDataToSend.append('content', formData.content);
    // formDataToSend.append('price', formData.price);
    // formDataToSend.append('discount', formData.discount);

    // Append only new image files
    images.forEach((image) => {
      if (image.isNew) {
        formDataToSend.append("productImage", image.file);
      }
    });
    formData?.categories.forEach((categoryData, index) => {
      formDataToSend.append(`categories[${index}]`, categoryData?.value);
    });

    // Append the list of removed image paths
    if (removedImages.length > 0) {
      formDataToSend.append("removedImages", JSON.stringify(removedImages));
    }

    setLoading(true);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/product/edit/${id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      CustomToast.success(response.data.message);
      navigate("/products");
    } catch (error) {
      if (error.response?.data?.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      content: content,
    }));
  };

  return (
    <Layout title="Edit Product">
      <main>
        <div className="card page-card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <Form.Group controlId="productImage">
                  <Form.Label>Product Image</Form.Label>
                  <ImageUpload
                    onFileSelect={handleImageSelect}
                    onDelete={handleImageDelete}
                    images={images}
                    multiple={false}
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group controlId="title">
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                {/* <Form.Group controlId="category">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group> */}
                <Form.Group controlId="categories">
                  <Form.Label>Categories</Form.Label>
                  <MultipleSelectDropdown
                    options={
                      categories?.map((item) => ({
                        value: item._id,
                        label: item.name,
                      })) || []
                    }
                    value={formData.categories}
                    isMulti
                    name="categories"
                    id="categories"
                    className="p-0"
                    placeholder="Select Categories"
                    isSearchable
                    onChange={(selectedItems, e) => {
                      setFormData((prev) => ({
                        ...prev,
                        categories: selectedItems,
                      }));
                    }}
                  />
                </Form.Group>
              </div>
              {/* <div className="col-12 col-lg-6">
                                <Form.Group controlId="email">
                                    <Form.Label>Original Price</Form.Label>
                                    <InputGroup className="custom-input-group">
                                        <InputGroup.Text id="basic-addon1">$</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleInputChange}
                                            placeholder="Enter product price"
                                            aria-label="Price"
                                            aria-describedby="basic-addon1"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </div> */}
              {/* <div className="col-12 col-lg-6">
                                <Form.Group controlId="discount">
                                    <Form.Label>Discount Price</Form.Label>
                                    <InputGroup className="custom-input-group">
                                        <InputGroup.Text id="basic-addon1">$</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleInputChange}
                                            placeholder="Enter product discount price"
                                            aria-label="Discount"
                                            aria-describedby="basic-addon1"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </div> */}
              <div className="col-12">
                <Form.Group controlId="Password">
                  <Form.Label>Link</Form.Label>
                </Form.Group>
                <input
                  type="text"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter product link"
                  id=""
                ></input>
              </div>
              {/* <div className="col-12">
                                <Form.Group controlId="Password">
                                    <Form.Label>Content</Form.Label>
                                </Form.Group>
                                <MyEditor
                                    htmlData={formData.content}
                                    setCourseAdd={(content) => {
                                        handleEditorChange(content);
                                    }}
                                />
                            </div> */}
              <div className="col-12 d-flex justify-content-end gap-3">
                <button
                  className="btn-outline fs-16 fw-700 px-4"
                  onClick={() => navigate("/products")}
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary fs-16 fw-700 px-4"
                  onClick={handleSubmit}
                  type="button"
                  disabled={loading}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default ProductsEdit;
